import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { queryClient } from "@/lib/query-client";
import { useAppStore } from "@/store/app-store";
import { usePendingBookings } from "@/hooks/usePendingBookings";
import { isBookingResponseWindowOpen } from "@/lib/booking-window";

export type AppNotification = {
  id: string;
  type: string;
  bookingId?: string;
  title: string;
  body: string;
  isRead: boolean;
  createdAt: string;
};

export const useNotifications = () => {
  const token = useAppStore((state) => state.token);
  return useQuery({
    queryKey: ["notifications"],
    queryFn: () => api<AppNotification[]>("/notifications"),
    enabled: Boolean(token),
  });
};

export const useMarkNotificationRead = () =>
  useMutation({
    mutationFn: (id: string) => api<AppNotification>(`/notifications/${id}/read`, { method: "PATCH" }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ["notifications"] }); },
  });

/**
 * A "new booking" notification is only actionable while the owner's response window is open.
 * Once it lapses (confirmed/rejected/expired), the notification bar should drop it rather than
 * leave a dead entry an owner can no longer act on.
 */
export const useVisibleNotifications = () => {
  const notifications = useNotifications();
  const pendingBookings = usePendingBookings();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const openBookingIds = new Set(
    (pendingBookings.data ?? [])
      .filter((booking) => booking.bookingStatus === "PENDING" && isBookingResponseWindowOpen(booking, now))
      .map((booking) => booking.id),
  );
  const data = notifications.data?.filter(
    (notification) => notification.type !== "NEW_BOOKING_REQUEST" || !notification.bookingId || openBookingIds.has(notification.bookingId),
  );

  return { ...notifications, data, openBookingIds };
};
